import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Chart,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Legend,
  Tooltip,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { analyzeCohorts } from "./analyze-cohorts";

Chart.register(CategoryScale, LinearScale, BarElement, Title, Legend, Tooltip);

function KpiCard({ value, label, color }) {
  return (
    <Card sx={{ flex: 1, textAlign: "center" }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h4" fontWeight={700} color={color || "primary"}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );
}

const acqChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: { mode: "index" },
  },
  scales: {
    y: {
      beginAtZero: true,
      stacked: true,
      ticks: { stepSize: 1, precision: 0 },
    },
    x: { stacked: true, ticks: { maxRotation: 45 } },
  },
};

const actChartOptions = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { position: "top" },
    tooltip: {
      mode: "index",
      callbacks: {
        afterBody: function (items) {
          const acq = items[0]?.raw || 0;
          const act = items[1]?.raw || 0;
          if (acq > 0) return "Taux: " + Math.round((act / acq) * 100) + "%";
          return "";
        },
      },
    },
  },
  scales: {
    y: { beginAtZero: true, ticks: { stepSize: 1, precision: 0 } },
    x: { ticks: { maxRotation: 45 } },
  },
};

export default function CohortsTab({
  contactsRows,
  contactsProRows,
  clientsRows,
  testsRows,
}) {
  const [period, setPeriod] = useState("monthly");
  const [includePros, setIncludePros] = useState(true);

  const analysis = useMemo(() => {
    if (!contactsRows && !clientsRows) return null;
    return analyzeCohorts({
      contactsRows: contactsRows || [],
      contactsProRows: contactsProRows || [],
      clientsRows: clientsRows || [],
      testsRows: testsRows || [],
    });
  }, [contactsRows, contactsProRows, clientsRows, testsRows]);

  if (!analysis) {
    return (
      <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
        Aucune donnée disponible.
      </Typography>
    );
  }

  const variant = analysis[period][includePros ? "all" : "nopro"];
  const k = variant.kpi;
  const { yearly } = analysis;

  // Acquisition chart datasets
  const acqDatasets = [
    {
      label: "Particuliers",
      data: variant.acqParticuliers,
      backgroundColor: "rgba(99,102,241,.7)",
      borderRadius: 4,
    },
  ];
  if (includePros) {
    acqDatasets.push({
      label: "Pros",
      data: variant.acqPros,
      backgroundColor: "rgba(245,158,11,.7)",
      borderRadius: 4,
    });
  }
  acqDatasets.push({
    label: "Clients",
    data: variant.acqClients,
    backgroundColor: "rgba(16,185,129,.7)",
    borderRadius: 4,
  });

  return (
    <Box>
      {/* === Controls === */}
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Période</InputLabel>
          <Select
            value={period}
            label="Période"
            onChange={(e) => setPeriod(e.target.value)}
          >
            <MenuItem value="monthly">Mensuel</MenuItem>
            <MenuItem value="quarterly">Trimestriel</MenuItem>
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Switch
              checked={includePros}
              onChange={(e) => setIncludePros(e.target.checked)}
            />
          }
          label="Inclure pros"
        />
      </Stack>

      {/* === Acquisition === */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Acquisition
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard value={k.acqTotal} label="Total avec date" />
        <KpiCard value={k.acqContacts} label="Contacts (prospects)" />
        <KpiCard value={k.acqClients} label="Clients" />
        <KpiCard value={k.acqNoDate} label="Sans date" />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <Bar
              height={350}
              data={{ labels: variant.labels, datasets: acqDatasets }}
              options={acqChartOptions}
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cohorte</TableCell>
                  <TableCell align="right">Contacts</TableCell>
                  <TableCell align="right">Clients</TableCell>
                  <TableCell align="right">Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variant.acqTable.map((r) => (
                  <TableRow key={r.period} hover>
                    <TableCell>{r.period}</TableCell>
                    <TableCell align="right">{r.contacts}</TableCell>
                    <TableCell align="right">
                      {r.clients}
                      {r.pct !== null && (
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ ml: 0.5 }}
                        >
                          ({r.pct}%)
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">{r.total}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableBody>
                <TableRow sx={{ fontWeight: 600 }}>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {k.acqContacts}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {k.acqClients}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {k.acqTotal}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    Sans date
                  </TableCell>
                  <TableCell
                    align="right"
                    colSpan={3}
                    sx={{ fontStyle: "italic", color: "text.secondary" }}
                  >
                    {k.acqNoDate}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* === Yearly Recap === */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Récap annuel
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Année</TableCell>
                  <TableCell align="right">Acquis</TableCell>
                  <TableCell align="right">Ventes</TableCell>
                  <TableCell align="right">Conversion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {yearly.rows.map((r) => (
                  <TableRow key={r.year} hover>
                    <TableCell>{r.year}</TableCell>
                    <TableCell align="right">{r.contacts}</TableCell>
                    <TableCell align="right">{r.sales}</TableCell>
                    <TableCell align="right">{r.conversion}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableBody>
                <TableRow sx={{ fontWeight: 600 }}>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {yearly.acqTotal}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {yearly.salesTotal}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {yearly.convTotal}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontStyle: "italic", color: "text.secondary" }}>
                    Sans date
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontStyle: "italic", color: "text.secondary" }}
                  >
                    {yearly.noDateContacts}
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontStyle: "italic", color: "text.secondary" }}
                  >
                    {yearly.noDateSales}
                  </TableCell>
                  <TableCell />
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* === Activation === */}
      <Typography variant="h6" sx={{ mb: 1.5 }}>
        Activation (essais)
      </Typography>

      <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
        <KpiCard
          value={k.actTotal}
          label="Personnes ayant testé"
          color="success.main"
        />
        <KpiCard
          value={k.actWith}
          label="Avec date d'acquisition"
          color="success.main"
        />
        <KpiCard value={k.actNo} label="Sans date d'acquisition" />
        <KpiCard
          value={`${k.actRate}%`}
          label="Taux d'activation"
          color="success.main"
        />
      </Stack>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Box>
            <Bar
              height={350}
              data={{
                labels: variant.labels,
                datasets: [
                  {
                    label: "Acquis",
                    data: variant.actAcquired,
                    backgroundColor: "rgba(229,231,235,.8)",
                    borderRadius: 4,
                  },
                  {
                    label: "Activés",
                    data: variant.actActivated,
                    backgroundColor: "rgba(5,150,105,.7)",
                    borderRadius: 4,
                  },
                ],
              }}
              options={actChartOptions}
            />
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Cohorte</TableCell>
                  <TableCell align="right">Acquis</TableCell>
                  <TableCell align="right">Activés</TableCell>
                  <TableCell align="right">Taux</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {variant.actTable.map((r) => (
                  <TableRow key={r.period} hover>
                    <TableCell>{r.period}</TableCell>
                    <TableCell align="right">{r.acquired}</TableCell>
                    <TableCell align="right">{r.activated}</TableCell>
                    <TableCell align="right">{r.rate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableBody>
                <TableRow sx={{ fontWeight: 600 }}>
                  <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {k.acqTotal}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {k.actWith}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>
                    {k.actRate}%
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
}
